import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { L10N_LOCALE, L10nLocale, L10nTranslationService } from 'angular-l10n';
import { Subscription } from 'rxjs';
import { AlertSettings, FeatureCollection, FeatureLayerSettings, MapApi, ShapeImageRequest } from 'src/app/hickory-api';
import { AuthService } from 'src/app/shared/authentication/auth.service';

interface AreaData {
  feature?: FeatureCollection;
  title: string;
  users: number;
  groups: number;
  assets: number;
  thumbUrl?: string;
}

const MAP_THUMB_WIDTH = 120;
const MAP_THUMB_HEIGHT = 90;

@Component({
  selector: 'app-recipient-details-areas',
  templateUrl: './recipient-details-areas.component.html',
  styleUrls: ['./recipient-details-areas.component.scss'],
  standalone: false
})
export class RecipientDetailsAreasComponent implements OnInit, OnDestroy {
  @Input() isReceived?: boolean = false;
  @Input() alert: AlertSettings;

  // Maps/Areas data
  areasLoading = 0;
  areas: AreaData[];
  geometries: string[] = new Array<string>();

  subscription = new Subscription();

  get isGroupFilteringPermitted(): boolean { return this.authService.hasPermission('map-filter-by-group.feature'); }

  constructor(
    @Inject(L10N_LOCALE) public locale: L10nLocale,
    private translation: L10nTranslationService,
    private authService: AuthService,
    private mapApi: MapApi,
  ) { }

  ngOnInit() {
    this.loadAreas();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  loadAreas() {
    const featureCollection = this.alert?.recipients?.geofences;
    const areas = new Array<AreaData>();
    const areaTitlePrefix = this.translation.translate('Area') + ' ';
    featureCollection?.features?.forEach((feature) => {
      const coords = feature.geometry.coordinates.toString();
      // don't re-process the same coordinates set more than once
      if (this.geometries.indexOf(coords) === -1) {
        this.geometries.push(coords);
      } else {
        return;
      }

      const index = areas.length;
      const singleFeatureCollection: FeatureCollection = { features: [feature], type: FeatureCollection.TypeEnum.FeatureCollection };
      let areaData: AreaData = { feature: singleFeatureCollection, title: areaTitlePrefix + (index + 1), users: -1, groups: -1, assets: -1, thumbUrl: null };
      areas[index] = areaData;

      // Don't call the mapApi.postFeaturesCount API here:
      //  1) we should be calling this API based on the featureLayers that were sent with the alert
      //  2) more to the point, this API shows the counts NOW (not when the alert was sent)

      const data: ShapeImageRequest = {
        width: MAP_THUMB_WIDTH,
        height: MAP_THUMB_HEIGHT,
        geoJson: singleFeatureCollection
      };
      this.areasLoading++;
      this.subscription.add(this.mapApi.getImagePreview(data).subscribe({
        next: res => {
          areaData = areas[index];
          // this allows this to work locally when the images are redirecting from an expired cert
          areaData.thumbUrl = res.ImageUrl?.replace('https', 'http');
          this.areas = areas;
          this.areasLoading--;
        }, error: e => {
          console.warn('error getting map image preview', e);
          this.areasLoading--;
        }
      }));
    });
  }

  getLayerRecipientCount(layer: FeatureLayerSettings): string {
    const count = layer.Count ?? 0;
    return count > 0 ? "" + count : this.translation.translate('None');
  }

  getLayerFilterText(layer: FeatureLayerSettings): string {
    const applicable: string[] = [
      FeatureLayerSettings.FeaturesSourceEnum.PublicUsers,
      FeatureLayerSettings.FeaturesSourceEnum.InternalUsers,
      FeatureLayerSettings.FeaturesSourceEnum.MultiDirectoryUsers,
    ];
    if (!applicable.includes(layer.FeaturesSource)) {
      return this.translation.translate('Alert.Map.AppliedLayerFilters', { filterCount: 'N/A' });
    } else {
      const count = (layer.FilterLegacyGroupIds?.length ?? 0) + (layer.FilterGroupIds?.length ?? 0);
      const filterCount = count > 0 ? '' + count : this.translation.translate('None');
      return this.translation.translate('Alert.Map.AppliedLayerFilters', { filterCount: filterCount });
    }
  }
}
